import { useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $downloadSepaStatementStore, $generateStatementOfFeesStore, $initialAccountsStore } from '@lib/store';
import { cn, DateUtils, PaletteUtils } from '@shared/utils';
import { type AccountResponse, AccountType, StatementExportFileType } from '@shared/api';
import { SelectAccount } from '@widgets';
import { DateRangePickerInput, Button, Text, Spinner, ErrorMessage, BaseInput, Tabs, Select } from '@shared/widgets';

export const StatementTabs = {
  Account: 'Account',
  Fee: 'Fee',
} as const;
export type StatementTabs = (typeof StatementTabs)[keyof typeof StatementTabs];

interface IForm {
  account: AccountResponse | null;
  // Account Statement Tab
  date: { startDate?: Dayjs; endDate?: Dayjs };
  fileType: StatementExportFileType;
  // Fee Statement Tab
  year?: string;
}
const NOW = dayjs();
const DEFAULT_VALUES: IForm = {
  account: null,
  date: { startDate: NOW, endDate: NOW },
  fileType: StatementExportFileType.Pdf,
  year: NOW.year().toString(),
};

export const StatementPaymentTab = () => {
  const accountStore = useUnit($initialAccountsStore.store);

  const [tab, setTab] = useState<StatementTabs>(StatementTabs.Account);

  const { t } = useTranslation('statements');
  const { control, formState, handleSubmit, watch, setValue } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const [account, year] = watch(['account', 'year']);

  useEffect(() => {
    if (accountStore.data.length > 0) setValue('account', accountStore.data[0]);
  }, [accountStore.data]);

  const yearOptions = useMemo(() => {
    const created = dayjs(account?.dateCreated);
    const startYear = created.isValid() ? created.year() : 2000;
    return DateUtils.getYearsList(startYear).map((year) => ({ label: year.toString(), value: year.toString() }));
  }, [account?.dateCreated]);
  useEffect(() => {
    if (yearOptions.length === 0) return;
    const currentYear = yearOptions.some((option) => option.value === year);
    if (!currentYear) setValue('year', yearOptions[0]!.value);
  }, [yearOptions, year, setValue]);

  const submitHandler = async (data: IForm) => {
    switch (tab) {
      case StatementTabs.Account:
        await $downloadSepaStatementStore.events.downloadSepaStatement({
          name: `${data.account?.accountNumber}_${dayjs(data.date.startDate).format('YYYY-MM-DD')}_${dayjs(data.date.endDate).format('YYYY-MM-DD')}`,
          accountId: data.account!.id,
          fileType: data.fileType,
          from: DateUtils.formatToIsoString(data.date.startDate!),
          to: DateUtils.formatToIsoString(data.date.endDate!),
        });
        break;
      case StatementTabs.Fee: {
        const feeYearDate = dayjs(`${data.year}-01-01`);
        const accountCreatedDate = dayjs(data.account?.dateCreated).startOf('day');
        const today = dayjs().startOf('day');

        let fromDate = feeYearDate.startOf('year');
        let toDate = feeYearDate.endOf('year');

        if (fromDate.isBefore(accountCreatedDate)) fromDate = accountCreatedDate;
        if (feeYearDate.year() === today.year() && toDate.isAfter(today)) toDate = today;
        if (toDate.isBefore(fromDate)) toDate = fromDate;

        await $generateStatementOfFeesStore.events.generateStatementOfFees({
          AccountId: data.account!.id,
          From: DateUtils.formatToIsoString(fromDate),
          To: DateUtils.formatToIsoString(toDate),
        });
        break;
      }
      default: {
        const never: never = tab;
        return never;
      }
    }
  };

  const tabs = useMemo(
    () => [
      { label: 'Account Statement', value: StatementTabs.Account },
      { label: 'Fee Statement', value: StatementTabs.Fee },
    ],
    [],
  );
  return (
    <form className='flex w-full flex-col' noValidate onSubmit={handleSubmit(submitHandler)}>
      <Spinner show={formState.isSubmitting} position />
      <div
        className={cn(
          'mb-6 bg-white p-6 dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'rounded-xl dark:rounded-xl dark:border dark:border-gray-black',
        )}
      >
        <Controller
          name='account'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div>
              <SelectAccount
                loading={accountStore.loading}
                type={AccountType.Payment}
                accounts={accountStore.data}
                value={value}
                onChange={onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      </div>
      {PaletteUtils.isInsigniaPalette && (
        <Tabs
          className='w-full'
          listClassName='gap-0'
          labelClassName='pt-4 px-6 pb-7 bg-white px-6 dark:bg-soft-black'
          options={tabs}
          value={tab}
          onChange={setTab}
        />
      )}
      <div
        className={cn(
          'flex w-full flex-col bg-white p-4 text-center dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'rounded-xl dark:rounded-xl dark:border dark:border-gray-black',
        )}
      >
        {PaletteUtils.isVcfcPalette && <Tabs className='mb-6 w-full' listClassName='gap-12' options={tabs} value={tab} onChange={setTab} />}
        {tab === StatementTabs.Account && (
          <>
            <Text className='mb-2' size='xs'>
              {t('payment.dateLabel')}
            </Text>
            <div className='flex justify-center gap-4'>
              <Controller
                name='date'
                control={control}
                render={({ field: { value }, fieldState: { error } }) => (
                  <BaseInput
                    inputClassName='opacity-100'
                    value={value.startDate?.format('DD.MM.YYYY')}
                    readOnly
                    error={!value.startDate ? error?.message : undefined}
                  />
                )}
              />
              <Controller
                name='date'
                control={control}
                render={({ field: { value }, fieldState: { error } }) => (
                  <BaseInput
                    inputClassName='opacity-100'
                    value={value.endDate?.format('DD.MM.YYYY')}
                    readOnly
                    error={!value.endDate ? error?.message : undefined}
                  />
                )}
              />
            </div>
            <Controller
              name='date'
              control={control}
              rules={{
                validate: (value) => {
                  if (!value?.startDate) return t('errors.form.required', { ns: 'common' });
                  if (!value?.endDate) return t('errors.form.required', { ns: 'common' });
                  return true;
                },
              }}
              render={({ field: { value, onChange } }) => (
                <DateRangePickerInput
                  className='mx-auto mt-3'
                  dateFrom={value.startDate}
                  dateTo={value.endDate}
                  minDate={dayjs(account?.dateCreated)}
                  maxDate={NOW}
                  onChange={(startDate, endDate) => onChange({ startDate, endDate })}
                />
              )}
            />
          </>
        )}
        {tab === StatementTabs.Fee && (
          <Controller
            name='year'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select className='mx-auto w-fit' items={yearOptions} value={value} onChange={onChange} error={error?.message} />
            )}
          />
        )}
        <Button className='mx-auto mt-7 w-full max-w-[408px]' color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('payment.buttons.generate')}
        </Button>
      </div>
    </form>
  );
};
